import { Box, Typography } from "@mui/material";
import clsx from "clsx";
import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";
import { LazyLoadImage } from "react-lazy-load-image-component";
import BriaButton from "../../../../../../components/common/BriaButton/BriaButton";
import { DEFAULT_STRUCTURE_REF_INFLUENCE } from "../../../../../../constants/TextToImageConstants";
import { useAppStore } from "../../../../../../hooks/useStores";
import { SyntheticEventProps } from "../../../../../../models/text-to-image";
import { createFileList, urlToFile } from "../../../../../../utils";
import styles from "./ImageReferenceGallery.module.scss";

export type ImageReferenceGalleryItemProps = {
	title: string;
	description?: string;
	images: { url: string; prompt: string; influence: number }[];
	onClose?: () => void;
	activeTab: string;
};

const ImageReferenceGalleryItem = ({ title, images, onClose, description }: ImageReferenceGalleryItemProps) => {
	const { t } = useTranslation("translation", { keyPrefix: "playground.textToImage.config.imageReference" });
	const { textToImageStore } = useAppStore();

	const handleImageClick = async (imageUrl: string, prompt: string, influence: number) => {
		try {
			const filename = imageUrl.split("/").pop() || "image";
			const file = await urlToFile(imageUrl, filename);
			const syntheticEvent: SyntheticEventProps = {
				target: {
					files: file ? createFileList([file]) : null,
					value: "",
				},
			};
			textToImageStore.handleUploadImage(syntheticEvent);

			textToImageStore.handleConfigChange("prompt", prompt);
			if (textToImageStore.config.image_reference?.structure_ref_influence === DEFAULT_STRUCTURE_REF_INFLUENCE) {
				textToImageStore.handleConfigChange("image_reference", {
					...textToImageStore.config.image_reference,
					structure_ref_influence: influence,
				});
			}
			onClose?.();
		} catch (error) {
			console.error("Error processing the image:", error);
		}
	};

	return (
		<Box className={styles.ImageReferenceGalleryItem}>
			<Typography className={styles.title}>{title}</Typography>
			{description && <Typography className={styles.description}>{description}</Typography>}
			<Box className={styles.images}>
				{images.map((image, index) => (
					<BriaButton
						key={index}
						className={clsx(styles.imageBtn, {
							[styles.active]:
								textToImageStore.imageReference?.file?.name &&
								image.url.includes(textToImageStore.imageReference?.file?.name || ""),
						})}
						onClick={() => handleImageClick(image.url, t(image.prompt), image.influence)}
					>
						<LazyLoadImage src={image.url} className={styles.imageItem} effect="blur" threshold={300} />
					</BriaButton>
				))}
			</Box>
		</Box>
	);
};

export default observer(ImageReferenceGalleryItem);
